import { useState } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";

const FAQS = [
  {
    q: "Where is the best study library in Noida?",
    a: "Justudy Library is widely regarded as one of the best study libraries in Noida, with two branches — Sector 22 Noida (open 24/7) and Sector 26 Noida (8 AM to 10 PM). Both branches offer silent AC study rooms, high-speed Wi-Fi, lockers, charging points on every desk, power backup, CCTV security and comfortable seating for serious students.",
  },
  {
    q: "Is there a 24/7 library in Sector 22 Noida?",
    a: "Yes. Justudy Library — Sector 22 Noida is open 24 hours a day, 7 days a week. Located at 2nd Floor, Moolchand Tower, Above Bank of India, Block I, Sector 22, Noida 201301. Ideal for UPSC, SSC, CAT, GATE aspirants and night-shift learners.",
  },
  {
    q: "What is the address of Justudy Library in Sector 26 Noida?",
    a: "Justudy Library — Sector 26 Noida is located at B-8, Sector 26, Noida, Uttar Pradesh 201301. Branch timings are 8:00 AM to 10:00 PM, daily.",
  },
  {
    q: "How do I find a study library near me in Noida?",
    a: "If you are searching for a library near you in Noida, Justudy Library has two convenient locations. Sector 22 branch is near ISKCON Temple Noida and is walking distance from Sector 15, 16, 18, 19. Sector 26 branch is near Botanical Garden metro and is walking distance from Sector 25, 27, 29. Both are easily accessible via Google Maps directions.",
  },
  {
    q: "What are the monthly fees for Justudy Library?",
    a: "Hall Membership starts at ₹1,250 per month (unreserved seat) or ₹1,500 (reserved seat). 3-month plans are ₹3,900, 6-month plans are ₹7,200. Deluxe Room is ₹1,800 monthly at Sector 22. Night Shift (9 PM – 7 AM) is ₹1,000 monthly. Weekend-only plan is ₹500 monthly. Hourly evening shifts start at ₹500 for 3 hours.",
  },
  {
    q: "Does Justudy Library offer Wi-Fi, lockers and AC?",
    a: "Yes. Both branches offer high-speed Wi-Fi, AC study rooms, personal lockers (₹150/month), 24/7 CCTV security, power backup, hot & cold water, LED lights, charging points on every desk and pinboards. Sector 22 also has kitchen and food ordering facility.",
  },
  {
    q: "Is Justudy Library good for UPSC, SSC, CAT or GATE preparation?",
    a: "Absolutely. Justudy Library is specifically designed for serious aspirants. The silent, disciplined environment, individual reserved seats with pinboards, long-hour access (24/7 at Sector 22), and ergonomic seating make it ideal for UPSC, SSC, CAT, GATE, CA, CS, banking and other competitive exam preparation.",
  },
  {
    q: "Is there a co-working space in Sector 22 Noida?",
    a: "Yes. Justudy doubles as a co-working space in Sector 22 Noida with high-speed Wi-Fi, network booster, charging points, AC rooms and the optional kitchen + food ordering facility — perfect for remote workers and freelancers in Noida.",
  },
  {
    q: "How can I book a seat at Justudy Library?",
    a: "Call us at +91 8447496808 or WhatsApp the same number to check current seat availability and book your seat. You can also fill out the contact form on this website and our team will reach out within an hour.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-24 md:py-32 bg-[#0B0B0B] border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="text-xs tracking-[0.25em] uppercase text-[#FACC15] font-semibold mb-4">
              Frequently Asked
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight font-semibold text-white leading-[1.05]">
              Questions about
              <br />
              <span className="text-[#FACC15] italic font-light">our libraries.</span>
            </h2>
            <p className="mt-6 text-white/55 text-sm md:text-base leading-relaxed max-w-md">
              Quick answers for students looking for the best <strong className="text-white/90">study library in Noida</strong>,
              including locations, fees, timings and facilities.
            </p>
            <div className="mt-8 inline-flex items-center gap-2 text-xs text-white/45 uppercase tracking-[0.2em]">
              <HelpCircle className="w-4 h-4 text-[#FACC15]" />
              Still have questions? Call +91 8447496808
            </div>
          </div>

          <div className="lg:col-span-7 space-y-2">
            {FAQS.map((f, i) => {
              const isOpen = open === i;
              return (
                <article
                  key={i}
                  data-testid={`faq-${i}`}
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                  className={`bg-[#1A1A1A] border rounded-sm transition-all ${
                    isOpen
                      ? "border-[#FACC15]/40"
                      : "border-white/5 hover:border-white/10"
                  }`}
                >
                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    className="w-full text-left flex items-center justify-between gap-4 p-5 md:p-6"
                  >
                    <h3
                      itemProp="name"
                      className="font-display text-base md:text-lg font-semibold text-white"
                    >
                      {f.q}
                    </h3>
                    <ChevronDown
                      className={`w-5 h-5 text-[#FACC15] flex-shrink-0 transition-transform ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  <div
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                    className={`grid transition-all duration-300 ${
                      isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p
                        itemProp="text"
                        className="px-5 md:px-6 pb-5 md:pb-6 text-sm md:text-[15px] text-white/70 leading-relaxed"
                      >
                        {f.a}
                      </p>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

export { FAQS };
